import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  Plus,
  Search,
  Workflow,
  Edit,
  Trash2,
  Copy,
  Clock,
  FileJson,
  Send,
  Loader2,
  AlertTriangle,
  X,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { toast } from "react-toastify";
import MetaFlowBuilder from "../components/flows/MetaFlowBuilder";
import { createStarterFlow, buildFlowJSON, uid } from "../utils/flowJsonBuilder";
import { FLOW_CATEGORIES } from "../utils/flowConstants";
import { usePhoneAccount } from "../contexts/PhoneAccountContext";
import PhoneSelector from "../components/PhoneSelector";
import {
  fetchWaFlows,
  getWaFlow,
  saveWaFlow,
  deleteWaFlow,
} from "../apis/whatsappFlows";

const STATUS_BADGE = {
  draft: { label: "DRAFT", cls: "bg-gray-100 text-gray-500" },
  published: { label: "PUBLISHED", cls: "bg-green-50 text-green-600" },
  failed: { label: "FAILED", cls: "bg-red-50 text-red-600" },
  pending_publish: { label: "PENDING", cls: "bg-yellow-50 text-yellow-600" },
};

const WhatsAppFlows = () => {
  const { selectedPhoneId, selectedPhoneAccount } = usePhoneAccount();
  const wabaId = selectedPhoneAccount?.wabaId || null;
  const phoneId = selectedPhoneId || null;

  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingFlow, setEditingFlow] = useState(null);
  const [mode, setMode] = useState("list");
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const latestFlowJsonRef = useRef(null);

  // Load flows from backend
  const loadFlows = useCallback(async () => {
    if (!phoneId) return;
    setLoading(true);
    try {
      const res = await fetchWaFlows({ phoneId });
      if (res.success) setFlows(res.flows || []);
    } catch (err) {
      console.error("Failed to load flows:", err);
    } finally {
      setLoading(false);
    }
  }, [phoneId]);

  useEffect(() => {
    loadFlows();
  }, [loadFlows]);

  const createNewFlow = useCallback(() => {
    const flow = createStarterFlow();
    setEditingFlow({
      _isNew: true,
      id: null,
      name: "New Flow",
      categories: ["LEAD_GENERATION"],
      flow_json: flow,
      meta_status: "draft",
    });
    setSubmitError(null);
    setMode("builder");
  }, []);

  const editFlow = useCallback(async (flowEntry) => {
    try {
      const res = await getWaFlow(flowEntry.id);
      if (res.success && res.flow) {
        const addUid = (obj) => {
          if (Array.isArray(obj)) return obj.map(addUid);
          if (obj && typeof obj === "object") {
            const out = { ...obj, _uid: uid() };
            Object.keys(out).forEach((k) => {
              if (Array.isArray(out[k])) out[k] = out[k].map(addUid);
            });
            return out;
          }
          return obj;
        };
        setEditingFlow({
          ...res.flow,
          flow_json: addUid(res.flow.flow_json),
        });
        setSubmitError(null);
        setMode("builder");
      }
    } catch (err) {
      toast.error("Failed to load flow for editing");
    }
  }, []);

  const handleSave = useCallback(
    async (publish = false) => {
      if (!editingFlow) return;
      if (!wabaId || !phoneId) {
        toast.error("Please select a WhatsApp account first");
        return;
      }

      const flowState = latestFlowJsonRef.current || editingFlow.flow_json;
      const sanitizedJson = buildFlowJSON(flowState);

      const setter = publish ? setPublishing : setSaving;
      setter(true);
      setSubmitError(null);

      try {
        const payload = {
          id: editingFlow._isNew ? undefined : editingFlow.id,
          name: editingFlow.name,
          categories: editingFlow.categories,
          flow_json: sanitizedJson,
          wabaId,
          phoneId,
          publish,
        };

        const res = await saveWaFlow(payload);

        if (res.success) {
          toast.success(publish ? "Flow published to Meta!" : "Flow saved");
          setMode("list");
          setEditingFlow(null);
          loadFlows();
        }
      } catch (err) {
        const errData = err.response?.data || {};
        const raw = errData.rawError || {};
        const primaryMsg = raw.error_user_msg || errData.error || errData.details || err.message || "Operation failed";
        const errorObj = {
          message: primaryMsg,
          title: raw.error_user_title || errData.errorTitle || null,
          step: errData.step || null,
          validationErrors: errData.validationErrors || null,
          rawError: raw,
        };
        setSubmitError(errorObj);

        if (errData.flow) {
          setEditingFlow((prev) => ({
            ...prev,
            ...errData.flow,
            _isNew: false,
            flow_json: prev.flow_json,
          }));
        }
      } finally {
        setter(false);
      }
    },
    [editingFlow, wabaId, phoneId, loadFlows]
  );

  const handleDelete = useCallback(
    async (id) => {
      if (!window.confirm("Delete this flow?")) return;
      try {
        await deleteWaFlow(id);
        setFlows((prev) => prev.filter((f) => f.id !== id));
        toast.success("Flow deleted");
      } catch (err) {
        toast.error("Failed to delete flow");
      }
    },
    []
  );

  const duplicateFlow = useCallback(
    async (flowEntry) => {
      try {
        const res = await getWaFlow(flowEntry.id);
        if (!res.success) return;
        const addUid = (obj) => {
          if (Array.isArray(obj)) return obj.map(addUid);
          if (obj && typeof obj === "object") {
            const out = { ...obj, _uid: uid() };
            Object.keys(out).forEach((k) => {
              if (Array.isArray(out[k])) out[k] = out[k].map(addUid);
            });
            return out;
          }
          return obj;
        };
        setEditingFlow({
          _isNew: true,
          id: null,
          name: `${flowEntry.name} (Copy)`,
          categories: res.flow.categories,
          flow_json: addUid(res.flow.flow_json),
          meta_status: "draft",
        });
        setSubmitError(null);
        setMode("builder");
      } catch {
        toast.error("Failed to duplicate flow");
      }
    },
    []
  );

  const importFlowJson = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json";
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const parsed = JSON.parse(ev.target.result);
          if (!parsed.version || !parsed.screens) return;
          const addUid = (obj) => {
            if (Array.isArray(obj)) return obj.map(addUid);
            if (obj && typeof obj === "object") {
              const out = { ...obj, _uid: uid() };
              Object.keys(out).forEach((k) => {
                if (Array.isArray(out[k])) out[k] = out[k].map(addUid);
              });
              return out;
            }
            return obj;
          };
          setEditingFlow({
            _isNew: true,
            id: null,
            name: file.name.replace(".json", ""),
            categories: ["OTHER"],
            flow_json: addUid(parsed),
            meta_status: "draft",
          });
          setSubmitError(null);
          setMode("builder");
        } catch {
          toast.error("Invalid JSON file");
        }
      };
      reader.readAsText(file);
    };
    input.click();
  }, []);

  // ────── Builder mode ──────

  if (mode === "builder" && editingFlow) {
    return (
      <div>
        {/* Top bar */}
        <div className="bg-white border-b border-gray-200 px-5 py-2 flex items-center gap-3 flex-wrap">
          <PhoneSelector compact section="flows" />
          <div className="w-px h-6 bg-gray-200" />
          <label className="text-[11px] font-semibold text-gray-500 shrink-0">Flow Name</label>
          <input
            value={editingFlow.name}
            onChange={(e) => setEditingFlow((prev) => ({ ...prev, name: e.target.value }))}
            className="flex-1 px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#048AAD]/40 max-w-xs"
            placeholder="Flow name"
          />
          <label className="text-[11px] font-semibold text-gray-500 shrink-0">Category</label>
          <select
            value={editingFlow.categories?.[0] || "LEAD_GENERATION"}
            onChange={(e) => setEditingFlow((prev) => ({ ...prev, categories: [e.target.value] }))}
            className="px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#048AAD]/40 bg-white"
          >
            {FLOW_CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>

          <div className="ml-auto flex items-center gap-2">
            {/* Save Draft */}
            <button
              onClick={() => handleSave(false)}
              disabled={saving || publishing}
              className="px-4 py-1.5 text-[11px] font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : null}
              Save Draft
            </button>
            {/* Save & Publish */}
            <button
              onClick={() => handleSave(true)}
              disabled={saving || publishing}
              className="px-4 py-1.5 text-[11px] font-semibold text-white bg-[#048AAD] rounded-lg hover:bg-[#037a9a] transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              {publishing ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
              Save & Publish
            </button>
          </div>
        </div>

        {/* Error banner */}
        {submitError && (
          <div className="mx-5 mt-3 border border-red-200 bg-red-50 rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-2 flex-1 min-w-0">
                <AlertTriangle size={16} className="text-red-500 shrink-0 mt-0.5" />
                <div className="min-w-0">
                  {submitError.title && (
                    <p className="text-xs font-semibold text-red-500 mb-0.5">{submitError.title}</p>
                  )}
                  <p className="text-sm font-semibold text-red-700">{submitError.message}</p>
                  {submitError.validationErrors && submitError.validationErrors.length > 0 && (
                    <div className="mt-2 space-y-1">
                      <p className="text-[11px] font-semibold text-red-600">Meta Validation Errors:</p>
                      {submitError.validationErrors.map((ve, i) => (
                        <div key={i} className="text-xs text-red-600 bg-red-100 rounded px-2 py-1 font-mono">
                          {ve.message || ve.error || JSON.stringify(ve)}
                          {ve.line_start && (
                            <span className="text-red-400 ml-2">(line {ve.line_start})</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {submitError.rawError && Object.keys(submitError.rawError).length > 0 && (
                    <details className="mt-2">
                      <summary className="text-[10px] text-red-400 cursor-pointer hover:text-red-600">Technical details</summary>
                      <pre className="text-[10px] text-red-500 bg-red-100 rounded p-2 mt-1 overflow-x-auto max-h-32">
                        {JSON.stringify(submitError.rawError, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              </div>
              <button onClick={() => setSubmitError(null)} className="p-1 text-red-400 hover:text-red-600 shrink-0">
                <X size={14} />
              </button>
            </div>
          </div>
        )}

        <MetaFlowBuilder
          initialFlow={editingFlow.flow_json}
          onChange={(flowState) => {
            latestFlowJsonRef.current = flowState;
          }}
          onSave={(flowState) => {
            latestFlowJsonRef.current = flowState;
            handleSave(false);
          }}
          onBack={() => {
            setMode("list");
            setEditingFlow(null);
            setSubmitError(null);
          }}
        />
      </div>
    );
  }

  // ────── List mode ──────

  const filtered = flows.filter((f) =>
    f.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#f8fafb] p-5">
      {/* Phone Account Selector */}
      <div className="mb-4">
        <PhoneSelector section="flows" />
      </div>

      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Workflow size={20} className="text-[#048AAD]" />
            WhatsApp Flows
          </h1>
          <p className="text-xs text-gray-500 mt-0.5">
            Create multi-screen forms for WhatsApp Business
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={importFlowJson}
            className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <FileJson size={14} /> Import JSON
          </button>
          <button
            onClick={createNewFlow}
            disabled={!phoneId}
            className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white bg-[#048AAD] rounded-lg hover:bg-[#037a9a] transition-colors shadow-sm disabled:opacity-50"
          >
            <Plus size={14} /> Create Flow
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="mb-4 relative max-w-sm">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search flows..."
          className="w-full pl-9 pr-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#048AAD]/40 bg-white"
        />
      </div>

      {/* Loading */}
      {loading && (
        <div className="text-center py-16">
          <Loader2 size={24} className="text-[#048AAD] animate-spin mx-auto mb-2" />
          <p className="text-xs text-gray-400">Loading flows...</p>
        </div>
      )}

      {/* Flows grid */}
      {!loading && filtered.length === 0 ? (
        <div className="text-center py-16">
          <Workflow size={40} className="text-gray-300 mx-auto mb-3" />
          <p className="text-sm font-semibold text-gray-500 mb-1">
            {flows.length === 0 ? "No flows yet" : "No matching flows"}
          </p>
          <p className="text-xs text-gray-400 mb-4">
            {flows.length === 0 ? "Create your first WhatsApp Flow to get started" : "Try a different search term"}
          </p>
          {flows.length === 0 && (
            <button
              onClick={createNewFlow}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white bg-[#048AAD] rounded-lg hover:bg-[#037a9a] transition-colors"
            >
              <Plus size={14} /> Create Flow
            </button>
          )}
        </div>
      ) : (
        !loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((flowEntry) => {
              const categoryLabel = FLOW_CATEGORIES.find((c) => c.value === flowEntry.categories?.[0])?.label || flowEntry.categories?.[0];
              const badge = STATUS_BADGE[flowEntry.meta_status] || STATUS_BADGE.draft;

              return (
                <div
                  key={flowEntry.id}
                  className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow group"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm font-bold text-gray-900 truncate">{flowEntry.name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] px-1.5 py-0.5 bg-[#048AAD]/10 text-[#048AAD] font-medium rounded">
                          {categoryLabel}
                        </span>
                        {flowEntry.meta_flow_id && (
                          <span className="text-[9px] text-gray-400 font-mono truncate max-w-[100px]" title={flowEntry.meta_flow_id}>
                            {flowEntry.meta_flow_id}
                          </span>
                        )}
                      </div>
                    </div>
                    <span className={`text-[9px] px-2 py-0.5 rounded-full font-semibold ${badge.cls}`}>
                      {badge.label}
                    </span>
                  </div>

                  {flowEntry.meta_status === "failed" && flowEntry.last_meta_error && (
                    <div className="mb-2 bg-red-50 border border-red-100 rounded px-2 py-1.5">
                      <p className="text-[10px] text-red-600 truncate" title={flowEntry.last_meta_error}>
                        <XCircle size={10} className="inline mr-1" />
                        {flowEntry.last_meta_error}
                      </p>
                    </div>
                  )}

                  {flowEntry.meta_status === "published" && (
                    <div className="mb-2 flex items-center gap-1 text-[10px] text-green-600">
                      <CheckCircle size={10} /> Published on Meta
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-gray-400 flex items-center gap-1">
                      <Clock size={10} />
                      {new Date(flowEntry.updated_at).toLocaleDateString()}
                    </span>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => editFlow(flowEntry)} className="p-1.5 text-gray-400 hover:text-[#048AAD] hover:bg-[#048AAD]/10 rounded-lg transition-colors" title="Edit">
                        <Edit size={13} />
                      </button>
                      <button onClick={() => duplicateFlow(flowEntry)} className="p-1.5 text-gray-400 hover:text-[#048AAD] hover:bg-[#048AAD]/10 rounded-lg transition-colors" title="Duplicate">
                        <Copy size={13} />
                      </button>
                      <button onClick={() => handleDelete(flowEntry.id)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Delete">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
};

export default WhatsAppFlows;
